import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, ElementRef, ViewChild, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, map, startWith, Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { DropdownOption } from '../../types/dropdownOption';
import { ApiService } from '../../api.service';
import { LocationName } from '../../types/locationName';
import { LocalizableComponent } from '../localizable/localizable.component';

@Component({
    selector: 'destination-multiselect',
    templateUrl: './destination-multiselect.component.html',
    styleUrls: ['./destination-multiselect.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DestinationMultiselectComponent extends LocalizableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fieldLabel: string = 'test';
  @Input() inputPlaceholder: string = '';
  @Input() options: DropdownOption[] = [];
  @Input() displayName: boolean = false;
  @Input() maxSelections: number = 5;
  @Output() onSelectedValuesChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  autoCompleteOptions: Observable<DropdownOption[]>;
  inputCtrl = new FormControl('');
  selectedValues: string[] = [];
  isInvalidValue: boolean = false;
  showDropdown: boolean = false;
  disconnect$: Subject<boolean> = new Subject<boolean>();
  highlightedIndex: number = -1;
  currentFilteredOptions: DropdownOption[] = [];

  @ViewChild('optionInput') optionInput!: ElementRef<HTMLInputElement>;

  displayData: { [value: string]: any } = {};

  constructor(private elementRef: ElementRef, private apiService: ApiService) {
    super();
    this.autoCompleteOptions = this.inputCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        if (!searchTerm || searchTerm.length === 0) {
          // No search term - return initial static options
          return of(this._filterStaticOptions(''));
        } else if (searchTerm.length >= 2) {
          // Search term provided - call API for dynamic results
          return this.apiService.searchAllLocations(searchTerm).pipe(
            map((locations: LocationName[]) => {
              const searchOptions = locations.map(location => ({
                name: this.formatLocationName(location),
                value: location.slicName
              }));
              // Combine with static options and remove duplicates
              return this._combineAndFilterOptions(searchOptions, searchTerm);
            })
          );
        } else {
          // Search term too short - filter static options
          return of(this._filterStaticOptions(searchTerm));
        }
      }),
      map((options: DropdownOption[]) => {
        const hasError = this.inputCtrl.value && options.length === 0;
        this.isInvalidValue = hasError;
        // Store current filtered options for keyboard navigation
        this.currentFilteredOptions = options;
        return options;
      })
    );
  }

  private formatLocationName(location: LocationName): string {
    if (!location || !location.slicName) return '';
    return location.slicName;
  }

  private _combineAndFilterOptions(searchOptions: DropdownOption[], searchTerm: string): DropdownOption[] {
    // Combine search results with static options, removing duplicates
    const allOptions = [...this.options];

    searchOptions.forEach(searchOption => {
      if (!allOptions.find(option => option.value === searchOption.value)) {
        allOptions.push(searchOption);
      }
    });

    // Filter by search term and exclude already selected
    const filterValue = searchTerm?.toLowerCase() ?? '';
    return allOptions
      .filter(option => option.name.toLowerCase().includes(filterValue))
      .filter(option => !this.selectedValues.includes(option.value));
  }

  private _filterStaticOptions(searchTerm: string): DropdownOption[] {
    const filterValue = searchTerm?.toLowerCase() ?? '';
    return this.options
      .filter(option => option.name.toLowerCase().includes(filterValue))
      .filter(option => !this.selectedValues.includes(option.value));
  }

  ngOnInit() {
    // Initialize with all options
    this.currentFilteredOptions = [...this.options];

    for (const option of this.options) {
      this.displayData[option.value] = {
        title: option.name,
        name: this.displayName ? option.name : option.value
      }
    }
  }

  ngOnChanges() {
    if (this.options) {
      for (const option of this.options) {
        this.displayData[option.value] = {
          title: option.name,
          name: this.displayName ? option.name : option.value
        }
      }
    }
  }

  ngOnDestroy() {
    this.disconnect$.next(true);
    this.disconnect$.unsubscribe();
  }

  add(value: string): void {
    if (value && this.selectedValues.length < this.maxSelections) {
      const index = this.options.findIndex((o: DropdownOption) => o.name.toLowerCase() === value.toLowerCase());
      if (index === -1) {
        this.isInvalidValue = true;
        return;
      }

      this.isInvalidValue = false;
      if (!this.selectedValues.includes(this.options[index].value)) {
        // Use immutable array operations
        this.selectedValues = [...this.selectedValues, this.options[index].value];
        this.onSelectedValuesChange.emit([...this.selectedValues]);
      }
    }

    this.inputCtrl.setValue(null);
    this.highlightedIndex = -1;
  }

  setSelectedValues(values: string[], emit = true) {
    this.selectedValues = [];
    if (!values.length) return;

    // Use immutable operations to build the selected values array
    const validValues: string[] = [];
    values.forEach(value => {
      const index = this.options.findIndex((o: DropdownOption) => o.value.toLowerCase() === value.toLowerCase());
      if (index !== -1) {
        validValues.push(this.options[index].value);
      }
    });

    this.selectedValues = [...validValues];
    if (emit) {
      this.onSelectedValuesChange.emit([...this.selectedValues]);
    }
  }

  remove(option: string): void {
    const index = this.selectedValues.indexOf(option);

    if (index >= 0) {
      // Use immutable array operations to avoid "Cannot delete property" errors
      this.selectedValues = this.selectedValues.filter(value => value !== option);
      this.onSelectedValuesChange.emit([...this.selectedValues]);
    }

    if (this.optionInput) {
      this.optionInput.nativeElement.value = '';
    }
    this.inputCtrl.setValue(null);
    this.isInvalidValue = false;
  }

  selected(option: DropdownOption): void {
    if (!this.selectedValues.includes(option.value) && this.selectedValues.length < this.maxSelections) {
      // Use immutable array operations
      this.selectedValues = [...this.selectedValues, option.value];
      this.onSelectedValuesChange.emit([...this.selectedValues]);
    }

    if (this.optionInput) {
      this.optionInput.nativeElement.value = '';
    }
    this.inputCtrl.setValue(null);
    this.isInvalidValue = false;
    this.showDropdown = false;
    this.highlightedIndex = -1;
  }

  onReset() {
    this.selectedValues = [];
    this.onSelectedValuesChange.emit([...this.selectedValues]);
    if (this.optionInput) {
      this.optionInput.nativeElement.value = '';
    }
    this.inputCtrl.setValue(null);
    this.isInvalidValue = false;
    this.showDropdown = false;
    this.highlightedIndex = -1;
  }

  onInputFocus() {
    this.showDropdown = true;
    this.highlightedIndex = -1;
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.showDropdown) return;

    // Use the stored filtered options for keyboard navigation
    const filteredOptions = this.currentFilteredOptions;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, filteredOptions.length - 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        break;

      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < filteredOptions.length) {
          this.selected(filteredOptions[this.highlightedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.showDropdown = false;
        this.highlightedIndex = -1;
        break;
    }
  }

  isMaxSelectionsReached(): boolean {
    return this.selectedValues.length >= this.maxSelections;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  trackByValue(index: number, option: DropdownOption): string {
    return option.value;
  }

  clearSelections(): void {
    this.selectedValues = [];
    this.inputCtrl.setValue('');
    this.onSelectedValuesChange.emit([]);
    this.showDropdown = false;
    this.highlightedIndex = -1;
  }

}
